import { Controller } from "@hotwired/stimulus"
import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import XHRUpload from '@uppy/xhr-upload'

// Import Uppy CSS
import '@uppy/core/dist/style.min.css'
import '@uppy/dashboard/dist/style.min.css'

export default class extends Controller {
  static targets = ["videoZone", "subtitleZone", "audioTrackZone"]
  static values = { 
    pageId: String,
    guideId: String,
    csrfToken: String
  }

  connect() {
    console.log('Uppy controller connected')
    this.initializeUppy()
  }

  disconnect() {
    if (this.videoUppy) this.videoUppy.destroy()
    if (this.subtitleUppy) this.subtitleUppy.destroy()
    if (this.audioTrackUppy) this.audioTrackUppy.destroy()
  }

  initializeUppy() {
    // Video Uppy - single file upload
    if (this.hasVideoZoneTarget) {
      this.videoUppy = new Uppy({
        restrictions: {
          allowedFileTypes: ['video/mp4', '.mp4'],
          maxFileSize: 3000 * 1024 * 1024, // 3000MB
          maxNumberOfFiles: 1
        },
        autoProceed: false
      })

      this.videoUppy.use(Dashboard, {
        inline: true,
        target: this.videoZoneTarget,
        hideUploadButton: false,
        note: 'Single MP4 video file only',
        height: 200,
        showRemoveButtonAfterComplete: true,
        proudlyDisplayPoweredByUppy: false
      })

      this.videoUppy.use(XHRUpload, {
        endpoint: `/guides/${this.guideIdValue}/pages/${this.pageIdValue}/upload_video`,
        fieldName: 'video',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })

      this.videoUppy.on('upload-success', (file, response) => {
        this.showMessage('Video uploaded successfully!', 'success')
        // Reload the page to show the new video
        setTimeout(() => window.location.reload(), 1000)
      })

      this.videoUppy.on('upload-error', (file, error) => {
        console.error('Video upload error:', error)
        this.showMessage('Video upload failed. Please try again.', 'error')
      })
    }

    // Audio Track Uppy - multiple files
    if (this.hasAudioTrackZoneTarget) {
      this.audioTrackUppy = new Uppy({
        restrictions: {
          allowedFileTypes: ['audio/mp3', 'audio/wav', 'audio/mpeg', '.mp3', '.wav'],
          maxFileSize: 50 * 1024 * 1024, // 50MB
          maxNumberOfFiles: 10
        },
        autoProceed: false
      })

      this.audioTrackUppy.use(Dashboard, {
        inline: true,
        target: this.audioTrackZoneTarget,
        hideUploadButton: false,
        note: 'MP3 or WAV audio files. Use naming: audio_en.mp3, audio_fr.mp3',
        height: 300,
        showRemoveButtonAfterComplete: true,
        proudlyDisplayPoweredByUppy: false
      })

      this.audioTrackUppy.use(XHRUpload, {
        endpoint: `/guides/${this.guideIdValue}/pages/${this.pageIdValue}/upload_audio_track`,
        fieldName: 'audio_track',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })

      // Handle individual file uploads
      this.audioTrackUppy.on('upload-success', (file, response) => {
        this.showMessage(`Audio track "${file.name}" uploaded successfully!`, 'success')
        // Refresh the audio track list dynamically instead of full page reload
        this.refreshAudioTracksList()
      })

      this.audioTrackUppy.on('upload-error', (file, error) => {
        console.error('Audio track upload error:', error)
        this.showMessage(`Audio track "${file.name}" upload failed. Please try again.`, 'error')
      })

      // Handle completion of all uploads
      this.audioTrackUppy.on('complete', (result) => {
        if (result.successful.length > 0) {
          this.showMessage(`${result.successful.length} audio track(s) uploaded successfully!`, 'success')
          // Refresh the page after a slight delay to show all new audio tracks
          setTimeout(() => window.location.reload(), 2000)
        }
      })
    }

    // Subtitle Uppy - multiple files
    if (this.hasSubtitleZoneTarget) {
      this.subtitleUppy = new Uppy({
        restrictions: {
          allowedFileTypes: ['text/vtt', '.vtt'],
          maxFileSize: 1024 * 1024, // 1MB
          maxNumberOfFiles: 10
        },
        autoProceed: false
      })

      this.subtitleUppy.use(Dashboard, {
        inline: true,
        target: this.subtitleZoneTarget,
        hideUploadButton: false,
        note: 'VTT subtitle files. Use naming: subtitles_en.vtt, subtitles_fr.vtt',
        height: 300,
        showRemoveButtonAfterComplete: true,
        proudlyDisplayPoweredByUppy: false
      })

      this.subtitleUppy.use(XHRUpload, {
        endpoint: `/guides/${this.guideIdValue}/pages/${this.pageIdValue}/upload_subtitle`,
        fieldName: 'subtitle',
        headers: {
          'X-CSRF-Token': this.csrfTokenValue
        }
      })

      // Handle individual file uploads
      this.subtitleUppy.on('upload-success', (file, response) => {
        this.showMessage(`Subtitle "${file.name}" uploaded successfully!`, 'success')
        // Refresh the subtitle list dynamically instead of full page reload
        this.refreshSubtitlesList()
      })

      this.subtitleUppy.on('upload-error', (file, error) => {
        console.error('Subtitle upload error:', error)
        this.showMessage(`Subtitle "${file.name}" upload failed. Please try again.`, 'error')
      })

      // Handle completion of all uploads
      this.subtitleUppy.on('complete', (result) => {
        if (result.successful.length > 0) {
          this.showMessage(`${result.successful.length} subtitle(s) uploaded successfully!`, 'success')
          // Refresh the page after a slight delay to show all new subtitles
          setTimeout(() => window.location.reload(), 2000)
        }
      })
    }
  }

  // Refresh subtitles list without full page reload
  refreshSubtitlesList() {
    // For now, we'll still do a page reload, but this could be improved
    // to dynamically update the subtitle list via Turbo
    setTimeout(() => window.location.reload(), 1500)
  }

  // Simple delete methods called by trash can buttons
  deleteVideo() {
    if (!confirm('Are you sure you want to delete this video?')) return

    fetch(`/guides/${this.guideIdValue}/pages/${this.pageIdValue}/delete_video`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': this.csrfTokenValue,
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.showMessage('Video deleted successfully!', 'success')
        setTimeout(() => window.location.reload(), 1000)
      } else {
        this.showMessage(data.error || 'Delete failed', 'error')
      }
    })
    .catch(error => {
      console.error('Delete error:', error)
      this.showMessage('Delete failed. Please try again.', 'error')
    })
  }

  deleteSubtitle(event) {
    const subtitleIndex = event.currentTarget.dataset.subtitleIndex
    
    if (!confirm('Are you sure you want to delete this subtitle?')) return

    fetch(`/guides/${this.guideIdValue}/pages/${this.pageIdValue}/delete_subtitle/${subtitleIndex}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': this.csrfTokenValue,
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.showMessage('Subtitle deleted successfully!', 'success')
        setTimeout(() => window.location.reload(), 1000)
      } else {
        this.showMessage(data.error || 'Delete failed', 'error')
      }
    })
    .catch(error => {
      console.error('Delete error:', error)
      this.showMessage('Delete failed. Please try again.', 'error')
    })
  }

  showMessage(message, type) {
    // Simple toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type === 'success' ? 'success' : 'error'} fixed top-4 right-4 z-50 max-w-sm`
    toast.innerHTML = `
      <div class="flex items-center">
        <iconify-icon icon="ph:${type === 'success' ? 'check-circle' : 'warning-circle'}" class="mr-2"></iconify-icon>
        ${message}
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast)
      }
    }, 3000)
  }

  refreshAudioTracksList() {
    // TODO: Implement dynamic refresh of audio tracks list
    // For now, just log that we would refresh
    console.log('Would refresh audio tracks list')
  }

  deleteAudioTrack(event) {
    const index = event.currentTarget.dataset.audioTrackIndex
    const confirmed = confirm('Are you sure you want to delete this audio track?')
    
    if (confirmed) {
      // TODO: Implement audio track deletion
      console.log(`Would delete audio track at index ${index}`)
      this.showMessage('Audio track deletion not yet implemented', 'warning')
    }
  }
} 